import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { generatePropertySchema, injectSchema, removeSchema } from '../utils/schemaGenerator';

// Constants
const ITEMS_PER_PAGE = 10;
const FETCH_TIMEOUT = 15000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Skeleton card component for loading state
const PropertySkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
    <div className="aspect-video bg-gray-200" />
    <div className="p-3 sm:p-4 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-5 bg-gray-200 rounded w-1/2" />
      <div className="flex gap-2">
        <div className="h-3 bg-gray-200 rounded w-12" />
        <div className="h-3 bg-gray-200 rounded w-12" />
      </div>
      <div className="h-3 bg-gray-200 rounded w-full" />
    </div>
  </div>
);

// Lazy loaded image component
const LazyImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (!src) {
    return (
      <div className={`relative ${className} bg-gradient-to-br from-emerald-50 to-emerald-100 flex flex-col items-center justify-center text-emerald-700`} data-testid="no-photo-placeholder">
        <i className="fas fa-home text-3xl mb-1 opacity-70"></i>
        <span className="text-xs font-medium">No photo yet</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <i className="fas fa-image text-gray-400 text-2xl"></i>
        </div>
      )}
      {error ? (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <i className="fas fa-home text-gray-400 text-3xl"></i>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
};

const Marketplace = () => {
  const navigate = useNavigate();
  const fetchControllerRef = useRef(null);
  const hasFetchedRef = useRef(false);
  
  // Get or create visitor ID for anonymous likes
  const getVisitorId = useCallback(() => {
    let visitorId = localStorage.getItem('flippyhomes_visitor_id');
    if (!visitorId) {
      visitorId = 'visitor_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('flippyhomes_visitor_id', visitorId);
    }
    return visitorId;
  }, []);

  // Get liked listings from localStorage
  const getLikedListings = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem('flippyhomes_liked_listings') || '[]');
    } catch {
      return [];
    }
  }, []);

  // Save liked listings to localStorage
  const saveLikedListings = useCallback((likedIds) => {
    localStorage.setItem('flippyhomes_liked_listings', JSON.stringify(likedIds));
  }, []);
  
  // Core state
  const [listings, setListings] = useState([]);
  const [likedListings, setLikedListings] = useState(getLikedListings());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Detail modal state
  const [selectedListing, setSelectedListing] = useState(null);
  const [fullListingData, setFullListingData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Form states
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageData, setMessageData] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    message: ''
  });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState({
    reason: '',
    description: '',
    reporterEmail: ''
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [reportingListing, setReportingListing] = useState(null);
  const [submittingReport, setSubmittingReport] = useState(false);

  // Share functions
  const handleShare = async (listing) => {
    const shareData = {
      title: listing.title,
      text: `Check out this property: ${listing.title} - R${listing.price?.toLocaleString()}`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      setShowShareModal(true);
    }
  };

  const shareToWhatsApp = (listing) => {
    const text = `🏠 ${listing.title}\n💰 R${listing.price?.toLocaleString()}\n\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
  };

  const shareToTwitter = (listing) => {
    const text = `Check out this property: ${listing.title} - R${listing.price?.toLocaleString()}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied!');
    setShowShareModal(false);
  };

  // Handle WhatsApp click on card
  const handleWhatsAppClick = (e, listing) => {
    e.stopPropagation(); // Prevent card click
    if (!listing.whatsappNumber) {
      toast.error('No WhatsApp number available for this listing');
      return;
    }
    const phoneNumber = listing.whatsappNumber.replace(/\D/g, '');
    const message = `Hi, I'm interested in this property on FlippyHomes: ${listing.title} - R${listing.price?.toLocaleString()}`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Handle report button click on card
  const handleReportClick = (e, listing) => {
    e.stopPropagation(); // Prevent card click
    setReportingListing(listing);
    setReportData({ reason: '', description: '', reporterEmail: '' });
    setShowReportModal(true);
  };

  // Submit report for a listing
  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!reportData.reason || !reportData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmittingReport(true);
    try {
      await axios.post(`${API}/reports`, {
        listingId: reportingListing.id,
        listingTitle: reportingListing.title,
        ...reportData
      });
      toast.success('Report submitted. We will review it shortly.');
      setShowReportModal(false);
      setReportingListing(null);
      setReportData({ reason: '', description: '', reporterEmail: '' });
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmittingReport(false);
    }
  };

  // Handle like/unlike a listing
  const handleLike = async (e, listingId) => {
    e.stopPropagation(); // Prevent card click
    
    const visitorId = getVisitorId();
    const isCurrentlyLiked = likedListings.includes(listingId);
    
    // Optimistic update
    let newLikedListings;
    if (isCurrentlyLiked) {
      newLikedListings = likedListings.filter(id => id !== listingId);
    } else {
      newLikedListings = [...likedListings, listingId];
    }
    setLikedListings(newLikedListings);
    saveLikedListings(newLikedListings);
    
    // Update listing likes count optimistically
    setListings(prev => prev.map(listing => {
      if (listing.id === listingId) {
        return {
          ...listing,
          likesCount: (listing.likesCount || 0) + (isCurrentlyLiked ? -1 : 1)
        };
      }
      return listing;
    }));
    
    try {
      await axios.post(`${API}/listings/${listingId}/like`, { visitorId });
    } catch (error) {
      // Revert on error
      console.error('Error liking listing:', error);
      setLikedListings(likedListings);
      saveLikedListings(likedListings);
      setListings(prev => prev.map(listing => {
        if (listing.id === listingId) {
          return {
            ...listing,
            likesCount: (listing.likesCount || 0) + (isCurrentlyLiked ? 1 : -1)
          };
        }
        return listing;
      }));
    }
  };

  // Fetch listings with server-side pagination and filtering
  const fetchListings = useCallback(async (page = 1, type = filterType, search = searchTerm, append = false, retryCount = 0) => {
    // Cancel any pending request
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();

    try {
      setLoading(true);
      if (retryCount === 0) {
        setError(null);
      }

      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out')), FETCH_TIMEOUT);
      });

      // Create fetch promise
      const fetchPromise = axios.post(`${API}/marketplace/listings`, {
        page,
        limit: ITEMS_PER_PAGE,
        type: type === 'all' ? null : type,
        search: search || null
      }, {
        signal: fetchControllerRef.current.signal
      });

      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      if (append) {
        setListings(prev => [...prev, ...response.data.listings]);
      } else {
        setListings(response.data.listings);
      }
      
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.total || 0);
      setHasMore(response.data.hasMore || false);
      setCurrentPage(page);
      setError(null);
      
    } catch (err) {
      if (axios.isCancel(err)) return;
      
      console.error('Error fetching listings:', err, `(attempt ${retryCount + 1}/${MAX_RETRIES})`);
      
      // Automatic retry on failure
      if (retryCount < MAX_RETRIES - 1) {
        console.log(`Retrying in ${RETRY_DELAY}ms...`);
        setTimeout(() => {
          fetchListings(page, type, search, append, retryCount + 1);
        }, RETRY_DELAY);
        return;
      }
      
      // All retries exhausted
      if (err.message === 'Request timed out') {
        setError('Loading took too long. Please check your connection and try again.');
      } else {
        setError('Failed to load properties. Please try again.');
      }
      
      if (!append) {
        setListings([]);
      }
    } finally {
      // Only set loading to false if we're not retrying
      if (retryCount >= MAX_RETRIES - 1 || !fetchControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [filterType, searchTerm]);

  // Initial fetch - prevent double fetch
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchListings(1);
    }
    
    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
      }
    };
  }, []);

  // Handle filter/search changes with debounce
  useEffect(() => {
    if (!hasFetchedRef.current) return;
    
    const timer = setTimeout(() => {
      fetchListings(1, filterType, searchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [filterType, searchTerm]);

  // Cleanup schema when modal closes
  useEffect(() => {
    if (!showDetailModal) {
      removeSchema();
      setFullListingData(null);
    }
  }, [showDetailModal]);

  // View listing details - fetch full data
  const handleViewDetails = async (listing) => {
    setSelectedListing(listing);
    setCurrentImageIndex(0);
    setShowMessageForm(false);
    setMessageData({ guestName: '', guestEmail: '', guestPhone: '', message: '' });
    setShowDetailModal(true);
    setLoadingDetails(true);

    try {
      // Fetch full listing details
      const response = await axios.get(`${API}/listings/${listing.id}`);
      setFullListingData(response.data);
      
      // Generate schema
      const ownerInfo = {
        userMode: response.data.ownerMode,
        fullName: response.data.companyName,
        email: '',
        agencyName: response.data.companyName,
        agencyAddress: response.data.workingAddress,
        city: '',
        postalCode: ''
      };
      const schemas = generatePropertySchema(response.data, ownerInfo);
      injectSchema(schemas);
    } catch (err) {
      console.error('Error fetching listing details:', err);
      // Use the preview data if full fetch fails
      setFullListingData(listing);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Message handling
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageData.guestName || !messageData.guestEmail || !messageData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/messages`, {
        listingId: selectedListing.id,
        ...messageData
      });
      toast.success('Message sent successfully!');
      setShowMessageForm(false);
      setMessageData({ guestName: '', guestEmail: '', guestPhone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Load more
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchListings(currentPage + 1, filterType, searchTerm, true);
    }
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Get current listing data (full or preview)
  const currentListingData = fullListingData || selectedListing;

  return (
    <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
          Property Marketplace
        </h1>
        <Button 
          onClick={() => navigate('/properties')}
          variant="outline"
          className="text-sm"
        >
          <i className="fas fa-search mr-2"></i>
          Advanced Search
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:mb-6">
        <div className="flex-1 relative">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <Input
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-10"
            data-testid="search-input"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-40 h-10" data-testid="filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="sale">For Sale</SelectItem>
            <SelectItem value="rental">To Rent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      {!loading && !error && (
        <p className="text-sm text-gray-500 mb-4">
          {totalCount > 0 ? `Showing ${listings.length} of ${totalCount} properties` : 'No properties found'}
        </p>
      )}

      {/* Error state */}
      {error && (
        <div className="text-center py-12" data-testid="error-state">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
            <i className="fas fa-exclamation-triangle text-2xl text-red-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Unable to Load Properties</h3>
          <p className="text-gray-500 mb-4 text-sm max-w-md mx-auto">{error}</p>
          <Button onClick={() => fetchListings(1)} data-testid="retry-btn">
            <i className="fas fa-redo mr-2"></i>
            Try Again
          </Button>
        </div>
      )}

      {/* Loading state - show skeletons */}
      {loading && listings.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="loading-skeletons">
          {[...Array(ITEMS_PER_PAGE)].map((_, i) => (
            <PropertySkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && listings.length === 0 && (
        <div className="text-center py-12" data-testid="empty-state">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <i className="fas fa-home text-2xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Properties Found</h3>
          <p className="text-gray-500 text-sm">Try adjusting your search or filters.</p>
        </div>
      )}

      {/* Property Grid */}
      {listings.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="property-grid">
            {listings.map((listing) => (
              <Card
                key={listing.id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handleViewDetails(listing)}
                data-testid="property-card"
              >
                {/* Image */}
                <div className="relative aspect-video overflow-hidden bg-gray-100">
                  <LazyImage
                    src={listing.thumbnail || (listing.images?.[0]?.thumbnail || listing.images?.[0]?.medium || listing.images?.[0])}
                    alt={listing.title}
                    className="w-full h-full"
                  />
                  
                  {/* Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      listing.type === 'sale' ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white'
                    }`}>
                      {listing.type === 'sale' ? 'For Sale' : 'To Rent'}
                    </span>
                  </div>

                  {/* Like Button */}
                  <button
                    onClick={(e) => handleLike(e, listing.id)}
                    className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                      likedListings.includes(listing.id)
                        ? 'bg-red-500 text-white'
                        : 'bg-white/90 text-gray-600 hover:bg-white hover:text-red-500'
                    }`}
                    data-testid="like-button"
                    title={likedListings.includes(listing.id) ? 'Unlike' : 'Like'}
                  >
                    <i className={`${likedListings.includes(listing.id) ? 'fas' : 'far'} fa-heart text-sm`}></i>
                  </button>

                  {/* Image count - moved to bottom right */}
                  {listing.imageCount > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                      <i className="fas fa-images mr-1"></i>{listing.imageCount}
                    </div>
                  )}
                </div>

                {/* Content */}
                <CardContent className="p-3 sm:p-4">
                  {/* Title and Likes */}
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-semibold text-gray-800 text-sm sm:text-base line-clamp-1 flex-1 mr-2">
                      {listing.title}
                    </h3>
                    {/* Likes count badge */}
                    {listing.likesCount > 0 && (
                      <span className="flex items-center gap-1 text-xs text-red-500 flex-shrink-0">
                        <i className="fas fa-heart"></i>
                        {listing.likesCount}
                      </span>
                    )}
                  </div>
                  
                  {/* Price */}
                  <p className="text-base sm:text-lg font-bold text-emerald-600 mb-1">
                    {formatPrice(listing.price)}
                    {listing.type === 'rental' && <span className="text-xs font-normal text-gray-500">/mo</span>}
                  </p>
                  
                  {/* Specs */}
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mb-1">
                    {listing.beds > 0 && (
                      <span><i className="fas fa-bed mr-1"></i>{listing.beds}</span>
                    )}
                    {listing.baths > 0 && (
                      <span><i className="fas fa-bath mr-1"></i>{listing.baths}</span>
                    )}
                    {listing.size > 0 && (
                      <span><i className="fas fa-expand mr-1"></i>{listing.size}m²</span>
                    )}
                  </div>

                  {/* Location */}
                  <p className="text-xs text-gray-400 line-clamp-1 mb-1" data-testid="property-card-location">
                    <i className="fas fa-map-marker-alt mr-1"></i>
                    {listing.location?.trim() || 'Location on request'}
                  </p>

                  {/* Rates & Levies Row (collapsed, minimal) */}
                  {(listing.ratesAndTaxes > 0 || listing.levies > 0) && (
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                      {listing.ratesAndTaxes > 0 && (
                        <span title="Rates & Taxes">
                          <i className="fas fa-file-invoice-dollar mr-1"></i>
                          R{listing.ratesAndTaxes.toLocaleString()}/mo
                        </span>
                      )}
                      {listing.levies > 0 && (
                        <span title="Levies">
                          <i className="fas fa-building mr-1"></i>
                          R{listing.levies.toLocaleString()}/mo
                        </span>
                      )}
                    </div>
                  )}

                  {/* Agent Name (NOT clickable) */}
                  <p className="text-xs text-gray-500 mb-2" data-testid="agent-name">
                    <i className="fas fa-user-tie mr-1"></i>
                    {listing.agentName || 'Property Agent'}
                  </p>

                  {/* Listing Reference */}
                  {listing.listingNumber && (
                    <p className="text-xs text-gray-300 mb-2">
                      Ref: {listing.listingNumber}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    {/* WhatsApp Button */}
                    {listing.whatsappNumber && (
                      <button
                        onClick={(e) => handleWhatsAppClick(e, listing)}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium py-2 px-2 rounded-md transition-colors"
                        data-testid="whatsapp-button"
                        title="WhatsApp Agent"
                      >
                        <i className="fab fa-whatsapp text-sm"></i>
                        <span className="hidden sm:inline">WhatsApp</span>
                      </button>
                    )}
                    
                    {/* Call Button */}
                    {(listing.agentPhone || listing.whatsappNumber) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `tel:${listing.agentPhone || listing.whatsappNumber}`;
                        }}
                        className="flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium py-2 px-2 rounded-md transition-colors"
                        data-testid="call-button"
                        title="Call Agent"
                      >
                        <i className="fas fa-phone text-xs"></i>
                        <span className="hidden sm:inline">Call</span>
                      </button>
                    )}
                    
                    {/* Report Button */}
                    <button
                      onClick={(e) => handleReportClick(e, listing)}
                      className="flex items-center justify-center gap-1 text-gray-400 hover:text-red-500 text-xs py-2 px-2 rounded-md transition-colors hover:bg-gray-50"
                      data-testid="report-button"
                      title="Report this listing"
                    >
                      <i className="fas fa-flag text-xs"></i>
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Load More / Loading indicator */}
          <div className="mt-6 text-center">
            {loading && listings.length > 0 && (
              <div className="flex justify-center items-center gap-2 text-gray-500">
                <i className="fas fa-spinner fa-spin"></i>
                <span className="text-sm">Loading more...</span>
              </div>
            )}
            
            {!loading && hasMore && (
              <Button
                onClick={handleLoadMore}
                variant="outline"
                className="px-8"
                data-testid="load-more-btn"
              >
                Load More Properties
              </Button>
            )}
            
            {!loading && !hasMore && listings.length > 0 && (
              <p className="text-sm text-gray-400">You've reached the end</p>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0 mx-2 sm:mx-4">
          <ScrollArea className="max-h-[90vh]">
            {selectedListing && (
              <div>
                {/* Image Gallery */}
                <div className="relative aspect-video bg-gray-100">
                  {loadingDetails ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="fas fa-spinner fa-spin text-3xl text-gray-400"></i>
                    </div>
                  ) : currentListingData?.images?.length > 0 ? (
                    <>
                      <img
                        src={typeof currentListingData.images[currentImageIndex] === 'string' 
                          ? currentListingData.images[currentImageIndex] 
                          : currentListingData.images[currentImageIndex]?.full || currentListingData.images[currentImageIndex]?.medium}
                        alt={currentListingData.title}
                        className="w-full h-full object-cover"
                      />
                      
                      {currentListingData.images.length > 1 && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev - 1 + currentListingData.images.length) % currentListingData.images.length); }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 sm:p-3 rounded-full"
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev + 1) % currentListingData.images.length); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 sm:p-3 rounded-full"
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                            {currentImageIndex + 1} / {currentListingData.images.length}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="fas fa-home text-5xl text-gray-300"></i>
                    </div>
                  )}
                </div>

                {/* Property Details */}
                <div className="p-4 sm:p-6">
                  <DialogHeader className="mb-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                      <div>
                        <span className={`inline-block text-xs font-bold px-2 py-1 rounded mb-2 ${
                          currentListingData?.type === 'sale' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {currentListingData?.type === 'sale' ? 'For Sale' : 'To Rent'}
                        </span>
                        <DialogTitle className="text-lg sm:text-xl">{currentListingData?.title}</DialogTitle>
                        <p className="text-gray-500 text-sm mt-1">
                          <i className="fas fa-map-marker-alt mr-1"></i>
                          {currentListingData?.propertyAddress || currentListingData?.location}
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-xl sm:text-2xl font-bold text-emerald-600">
                          {formatPrice(currentListingData?.price || 0)}
                        </p>
                        {currentListingData?.type === 'rental' && (
                          <span className="text-gray-500 text-sm">per month</span>
                        )}
                      </div>
                    </div>
                  </DialogHeader>

                  {/* Specs */}
                  {currentListingData?.specifications && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg mb-4">
                      {currentListingData.specifications.beds > 0 && (
                        <div className="text-center">
                          <i className="fas fa-bed text-xl text-emerald-600"></i>
                          <p className="font-semibold text-sm">{currentListingData.specifications.beds}</p>
                          <p className="text-xs text-gray-500">Beds</p>
                        </div>
                      )}
                      {currentListingData.specifications.baths > 0 && (
                        <div className="text-center">
                          <i className="fas fa-bath text-xl text-emerald-600"></i>
                          <p className="font-semibold text-sm">{currentListingData.specifications.baths}</p>
                          <p className="text-xs text-gray-500">Baths</p>
                        </div>
                      )}
                      {currentListingData.specifications.floorSize > 0 && (
                        <div className="text-center">
                          <i className="fas fa-expand text-xl text-emerald-600"></i>
                          <p className="font-semibold text-sm">{currentListingData.specifications.floorSize}m²</p>
                          <p className="text-xs text-gray-500">Floor</p>
                        </div>
                      )}
                      {currentListingData.specifications.propertySize > 0 && (
                        <div className="text-center">
                          <i className="fas fa-ruler-combined text-xl text-emerald-600"></i>
                          <p className="font-semibold text-sm">{currentListingData.specifications.propertySize}m²</p>
                          <p className="text-xs text-gray-500">Property</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  {currentListingData?.description && (
                    <div className="mb-4">
                      <h3 className="font-semibold mb-2 text-sm">Description</h3>
                      <p className="text-gray-600 text-sm whitespace-pre-wrap">{currentListingData.description}</p>
                    </div>
                  )}

                  {/* Amenities & Features (handles new + legacy keys, plus customAmenities) */}
                  {(() => {
                    const a = currentListingData?.amenities || {};
                    const t = (...keys) => keys.some(k => a?.[k]);
                    const items = [];
                    if (t('pool', 'hasPool')) items.push({ label: 'Swimming Pool', icon: 'fa-swimming-pool', color: 'text-blue-500', bg: 'bg-blue-50' });
                    if (t('garden', 'hasGarden')) items.push({ label: 'Garden', icon: 'fa-leaf', color: 'text-green-500', bg: 'bg-green-50' });
                    if (t('security')) items.push({ label: 'Security', icon: 'fa-shield-alt', color: 'text-gray-600', bg: 'bg-gray-50' });
                    if (t('garage', 'hasGarage')) items.push({ label: 'Garage', icon: 'fa-car', color: 'text-slate-500', bg: 'bg-slate-50' });
                    if (t('airConditioning')) items.push({ label: 'Air Conditioning', icon: 'fa-snowflake', color: 'text-cyan-500', bg: 'bg-cyan-50' });
                    if (t('petFriendly', 'isPetFriendly')) items.push({ label: 'Pet Friendly', icon: 'fa-paw', color: 'text-yellow-600', bg: 'bg-yellow-50' });
                    if (t('fibre', 'hasFiber')) items.push({ label: 'Fibre Internet', icon: 'fa-wifi', color: 'text-indigo-500', bg: 'bg-indigo-50' });
                    if (t('borehole')) items.push({ label: 'Borehole', icon: 'fa-tint', color: 'text-blue-600', bg: 'bg-blue-50' });
                    if (t('solarPanels')) items.push({ label: 'Solar Panels', icon: 'fa-solar-panel', color: 'text-yellow-500', bg: 'bg-yellow-50' });
                    if (t('staffAccommodation')) items.push({ label: 'Staff Quarters', icon: 'fa-home', color: 'text-purple-500', bg: 'bg-purple-50' });
                    const customs = Array.isArray(currentListingData?.customAmenities) ? currentListingData.customAmenities : [];
                    customs.forEach(c => items.push({ label: c, icon: 'fa-check-circle', color: 'text-emerald-500', bg: 'bg-emerald-50', custom: true }));

                    if (items.length === 0) return null;
                    return (
                      <div className="mb-4" data-testid="marketplace-amenities">
                        <h3 className="font-semibold mb-2 text-sm">Amenities & Features</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {items.map((item, i) => (
                            <div
                              key={i}
                              className={`flex items-center gap-2 ${item.bg} rounded-lg px-3 py-2`}
                            >
                              <i className={`fas ${item.icon} ${item.color} text-base`}></i>
                              <span className="text-xs font-medium text-gray-700 line-clamp-2">{item.label}</span>
                              <i className="fas fa-check-circle text-emerald-500 text-xs ml-auto" aria-hidden="true"></i>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Contact Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {currentListingData?.whatsappNumber && (
                      <Button
                        className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-sm"
                        onClick={() => window.open(`https://wa.me/${currentListingData.whatsappNumber.replace(/\D/g, '')}?text=Hi, I'm interested in: ${currentListingData.title}`, '_blank')}
                      >
                        <i className="fab fa-whatsapp mr-2"></i>WhatsApp
                      </Button>
                    )}
                    {currentListingData?.contactNumber && (
                      <Button
                        variant="outline"
                        className="flex-1 sm:flex-none text-sm"
                        onClick={() => window.open(`tel:${currentListingData.contactNumber}`)}
                      >
                        <i className="fas fa-phone mr-2"></i>Call
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="text-sm"
                      onClick={() => handleShare(currentListingData)}
                    >
                      <i className="fas fa-share-alt mr-2"></i>Share
                    </Button>
                    <Button
                      variant="outline"
                      className="text-sm"
                      onClick={() => navigate('/home-services')}
                    >
                      <i className="fas fa-tools mr-2"></i>Contractors
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={(open) => {
        setShowReportModal(open);
        if (!open) setReportingListing(null);
      }}>
        <DialogContent className="max-w-md mx-2">
          <DialogHeader>
            <DialogTitle>Report Listing</DialogTitle>
            {reportingListing && (
              <p className="text-sm text-gray-500 mt-1">
                Reporting: {reportingListing.title}
              </p>
            )}
          </DialogHeader>
          <form onSubmit={handleSubmitReport} className="space-y-4">
            <div>
              <Label>Reason *</Label>
              <Select value={reportData.reason} onValueChange={(v) => setReportData({...reportData, reason: v})}>
                <SelectTrigger data-testid="report-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scam">Scam / Fraudulent Listing</SelectItem>
                  <SelectItem value="inaccurate">Incorrect Information</SelectItem>
                  <SelectItem value="spam">Spam / Duplicate</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate Content</SelectItem>
                  <SelectItem value="sold">Property Already Sold/Rented</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={reportData.description}
                onChange={(e) => setReportData({...reportData, description: e.target.value})}
                placeholder="Please describe the issue in detail..."
                rows={3}
                data-testid="report-description"
              />
            </div>
            <div>
              <Label>Your Email (optional)</Label>
              <Input
                type="email"
                value={reportData.reporterEmail}
                onChange={(e) => setReportData({...reportData, reporterEmail: e.target.value})}
                placeholder="We may contact you for more details"
                data-testid="report-email"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowReportModal(false)} disabled={submittingReport}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-red-600 hover:bg-red-700"
                disabled={submittingReport}
                data-testid="submit-report-btn"
              >
                {submittingReport ? (
                  <><i className="fas fa-spinner fa-spin mr-2"></i>Submitting...</>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-sm mx-2">
          <DialogHeader>
            <DialogTitle>Share This Property</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-3">
            <button onClick={() => shareToWhatsApp(selectedListing)} className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-100">
              <i className="fab fa-whatsapp text-2xl text-green-600"></i>
              <span className="text-xs">WhatsApp</span>
            </button>
            <button onClick={shareToFacebook} className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-100">
              <i className="fab fa-facebook text-2xl text-blue-600"></i>
              <span className="text-xs">Facebook</span>
            </button>
            <button onClick={() => shareToTwitter(selectedListing)} className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-100">
              <i className="fab fa-twitter text-2xl text-blue-400"></i>
              <span className="text-xs">Twitter</span>
            </button>
            <button onClick={copyLink} className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-100">
              <i className="fas fa-link text-2xl text-gray-600"></i>
              <span className="text-xs">Copy Link</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Marketplace;
